import React, { useEffect, useState } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import axios from 'axios';
import {
  Chart,
  ArcElement,
  PointElement,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register elements
Chart.register(ArcElement, PointElement, LineElement, BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

interface BattleStats {
  totalBattles: number;
  avgDuration: number;
  recentBattles: number;
  todaysBattles: number;
  battleTypes: {
    duel: number;
    tournament: number;
    teamBattle: number;
    beast: number;
    other: number;
  };
  winRatio: number;
}

const BattleMetrics: React.FC = () => {
  const [battleStats, setBattleStats] = useState<BattleStats>({
    totalBattles: 0,
    avgDuration: 0,
    recentBattles: 0,
    todaysBattles: 0,
    battleTypes: {
      duel: 0,
      tournament: 0,
      teamBattle: 0,
      beast: 0,
      other: 0,
    },
    winRatio: 0,
  });

  useEffect(() => {
    const fetchBattleStats = async () => {
      try {
        const response = await axios.get('/api/admin/battle-stats');
        setBattleStats(response.data);
      } catch (error) {
        console.error('Error fetching battle statistics', error);
      }
    };

    fetchBattleStats();
  }, []);

  // Battle activity data
  const activityData = {
    labels: ['Total Battles', 'Recent (7 days)', 'Today'],
    datasets: [
      {
        label: 'Battle Activity',
        backgroundColor: ['#00bfff', '#ffdd57', '#32cd32'],
        data: [battleStats.totalBattles, battleStats.recentBattles, battleStats.todaysBattles],
      },
    ],
  };

  // Battle types pie chart
  const typeData = {
    labels: ['Duels', 'Tournaments', 'Team Battles', 'Beast Battles', 'Other'],
    datasets: [
      {
        data: [
          battleStats.battleTypes.duel,
          battleStats.battleTypes.tournament,
          battleStats.battleTypes.teamBattle,
          battleStats.battleTypes.beast,
          battleStats.battleTypes.other,
        ],
        backgroundColor: ['#ff6384', '#36a2eb', '#ffce56', '#4bc0c0', '#9966ff'],
      },
    ],
  };

  return (
    <>
      <h3>Combat Statistics</h3>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '15px' }}>
          <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#1c1c29', borderRadius: '8px', border: '1px solid #6d4e27' }}>
            <h4 style={{ margin: '0 0 5px 0', color: '#ffd700', fontSize: '0.9rem' }}>Total Battles</h4>
            <p style={{ margin: 0, fontSize: '1.4em', fontWeight: 'bold', color: '#f8f0c6' }}>{battleStats.totalBattles}</p>
          </div>
          <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#1c1c29', borderRadius: '8px', border: '1px solid #6d4e27' }}>
            <h4 style={{ margin: '0 0 5px 0', color: '#ffd700', fontSize: '0.9rem' }}>Avg Duration</h4>
            <p style={{ margin: 0, fontSize: '1.4em', fontWeight: 'bold', color: '#f8f0c6' }}>{battleStats.avgDuration} rounds</p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', height: '280px' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#ffd700', fontSize: '1rem', textAlign: 'center' }}>Battle Activity</h4>
          <div style={{ flex: 1 }}>
            <Bar
              data={activityData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { color: '#f8f0c6' },
                    grid: { color: '#6d4e27' }
                  },
                  x: {
                    ticks: { color: '#f8f0c6' },
                    grid: { color: '#6d4e27' }
                  }
                }
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#ffd700', fontSize: '1rem', textAlign: 'center' }}>Battle Types</h4>
          <div style={{ flex: 1 }}>
            <Pie
              data={typeData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: { color: '#f8f0c6', font: { size: 10 } }
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default BattleMetrics;
